import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
  inject,
} from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { CdkMenu, CdkMenuItem, CdkMenuTrigger } from '@angular/cdk/menu';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import {
  captureFieldTypes,
  captureFormIssues,
  isUnsupportedCaptureForm,
} from './workflow-capture-form-validation';
import type {
  WorkflowCaptureField,
  WorkflowCaptureForm,
} from '@app/shared/workflow-commands/workflow-capture-form';
import {
  resolveWorkflowFormLayout,
  type WorkflowFormLayout,
} from '@app/shared/workflow-commands/workflow-form-layout';
import {
  workflowFormLayoutIssue,
  workflowFormWidthChoices,
} from './workflow-form-layout-authoring';

export interface WorkflowCaptureFormCopy {
  key: string;
  label: string;
  form: WorkflowCaptureForm;
}
interface ChoiceOrigin { formId: string; version: number; fieldId: string; index: number; value: string; label: string; owner: number; }
interface RefusedDependency { ids: string[]; form: string; owner: string | null; selection: string | null; }
const fieldKeys = new Set([
  'id',
  'label',
  'type',
  'required',
  'maxLength',
  'minimum',
  'maximum',
  'options',
  'visibleWhen',
  'layout',
  'lookup',
]);

/** Local authoring only. Module publication and submission checks remain authoritative. */
@Component({
  selector: 'phms-workflow-capture-form-editor',
  standalone: true,
  imports: [
    UiButtonDirective,
    UiControlDirective,
    CommonModule,
    FormsModule,
    DragDropModule, CdkMenu, CdkMenuItem, CdkMenuTrigger,
  ],
  templateUrl: './workflow-capture-form-editor.component.html',
  styleUrl: './workflow-capture-form-editor.component.scss',
})
export class WorkflowCaptureFormEditorComponent
  implements OnChanges, AfterViewChecked
{
  @Input() form: WorkflowCaptureForm | null = null;
  @Input() copies: WorkflowCaptureFormCopy[] = [];
  @Input() disabled = false;
  @Input() inspectorMode = false;
  @Input() showAddField = true;
  @Input() selectedFieldId: string | null = null;
  /** The document owner explicitly enabled the compatible versioned layout contract. */
  @Input() layoutEnabled = false;
  @Input() managedLayout = false;
  @Input() lookupSourceAvailable = false;
  @Input() lookupSourceReason = '';
  /** Live owning draft/actor identity. Reconciliation, not a stale gesture, adopts a new owner. */
  @Input() editContext: (() => string) | null = null;
  @Input() activeField: (() => string | null) | null = null;
  /** Transient mounting/loading/navigation state does not change the adopted owner. */
  @Input() editAvailable: (() => boolean) | null = null;
  @Output() fieldSelectionChange = new EventEmitter<string | null>();
  @Output() layoutChangeRequested = new EventEmitter<{ fieldId: string; change: Partial<WorkflowFormLayout> }>();
  @Output() formChange = new EventEmitter<WorkflowCaptureForm>();
  @Output() lookupSourceRequested = new EventEmitter<string>();
  @Output() inlineSourceRequested = new EventEmitter<string>();
  readonly types = captureFieldTypes;
  readonly widthChoices = workflowFormWidthChoices;
  selectedField = '';
  selectedCopy = '';
  error = '';
  private context = '';
  private editingContext: string | null = null;
  private ownerContextRegistered = false;
  private ownerGeneration = 0;
  private reconcileNativeControls = false;
  private recovery: RefusedDependency | null = null;
  @ViewChildren('fieldTypeModel') private fieldTypeModels!: QueryList<NgModel>;
  @ViewChildren('fieldTypeModel', { read: ElementRef }) private fieldTypeElements!: QueryList<ElementRef<HTMLSelectElement>>;
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private pendingFocus: { id: string; label: boolean; visibility?: boolean; owner: string | null; form: string } | null = null;
  private focusRequest = 0;
  get unsupported(): boolean {
    return isUnsupportedCaptureForm(this.form);
  }
  get fields(): WorkflowCaptureField[] {
    return this.unsupported ? [] : (this.form?.fields ?? []);
  }
  private ownerCurrent(): boolean { return !this.editContext || this.editingContext === this.editContext(); }
  private get editReady(): boolean { return !this.editAvailable || this.editAvailable(); }
  private get editable(): boolean { return !this.disabled && !this.unsupported && this.ownerCurrent() && this.editReady; }
  private fieldActive(id: string): boolean { return !this.inspectorMode || !this.activeField || this.activeField() === id; }
  private currentSelection(): string | null { return this.inspectorMode ? this.activeField ? this.activeField() : this.selectedFieldId : this.selectedField || null; }
  get recoveryFields(): WorkflowCaptureField[] {
    const recovery = this.recovery;
    return recovery && this.error && this.editable && recovery.owner === this.editingContext && recovery.form === JSON.stringify(this.form) && recovery.selection === this.currentSelection()
      ? this.fields.filter(field => recovery.ids.includes(field.id)) : [];
  }
  get hasUnknownSettings(): boolean {
    return (
      !!this.form &&
      !this.unsupported &&
      (Object.keys(this.form).some(
        (key) => !['version', 'id', 'title', 'fields'].includes(key),
      ) ||
        this.fields.some(
          (field) =>
            Object.keys(field).some((key) => !fieldKeys.has(key)) ||
            !this.knownType(field),
        ))
    );
  }
  get validation() {
    return captureFormIssues(this.form);
  }
  get formIssue(): string {
    return !this.form || this.unsupported ? '' : this.validation.form;
  }
  get visibleFields() {
    return this.inspectorMode
      ? this.fields.filter((field) => field.id === this.selectedFieldId)
      : this.fields;
  }
  get formSettingsVisible() {
    return !this.inspectorMode || this.selectedFieldId === null;
  }
  get invalidFields() {
    return this.fields.filter((field) => this.issue(field));
  }
  fieldLabel(field: WorkflowCaptureField) {
    return field.label.trim() || 'Untitled field';
  }
  fieldPosition(field: WorkflowCaptureField) {
    return this.fields.findIndex((item) => item.id === field.id);
  }
  selectField(id: string | null): void {
    if (id !== null && !this.fields.some((field) => field.id === id)) return;
    this.selectedField = id ?? '';
    if (this.inspectorMode) this.selectedFieldId = id;
    this.fieldSelectionChange.emit(id);
  }
  ngOnChanges(changes: SimpleChanges = {}): void {
    const previousOwner = this.editingContext;
    if (changes['form']) this.reconcileNativeControls = true;
    if (changes['form'] || changes['editContext'] && !this.ownerContextRegistered && this.editContext) {
      this.editingContext = this.editContext?.() ?? null; this.ownerGeneration++;
      this.ownerContextRegistered ||= !!this.editContext;
    }
    if (previousOwner !== this.editingContext || this.recovery && this.recovery.form !== JSON.stringify(this.form)) {
      this.error = ''; this.recovery = null; this.pendingFocus = null; this.focusRequest++;
    }
    const context = this.form?.id ?? '';
    if (context !== this.context) {
      this.context = context;
      this.selectedCopy = '';
      this.error = '';
      this.recovery = null;
      this.selectedField = '';
      this.pendingFocus = null;
      this.focusRequest++;
    }
    if (this.inspectorMode) this.selectedField = this.selectedFieldId ?? '';
    else if (!this.fields.some((field) => field.id === this.selectedField))
      this.selectedField = '';
  }
  ngAfterViewChecked(): void {
    if (this.reconcileNativeControls) {
      this.reconcileNativeControls = false;
      this.restoreReconciledNativeControls();
    }
    if (!this.pendingFocus) return;
    const { id, label, visibility, owner, form } = this.pendingFocus;
    const field = Array.from(
      this.element.nativeElement.querySelectorAll<HTMLElement>(
        '[data-capture-field]',
      ),
    ).find((item) => item.dataset['captureField'] === id);
    const target = field?.querySelector<HTMLElement>(
      visibility ? '[data-field-setting="visibility"]' : label ? '[data-field-setting="label"]' : '.field-content',
    );
    if (!target) return;
    this.pendingFocus = null;
    const request = ++this.focusRequest,
      formId = this.form?.id,
      activeElement = target.ownerDocument.activeElement;
    // NgModel writes its initial value in a microtask, which otherwise resets
    // a selection made during the render pass. Cancel if focus moved meanwhile.
    queueMicrotask(() => {
      const currentField = this.fields.find((item) => item.id === id);
      const currentActive = target.ownerDocument.activeElement;
      // A visibility handoff removes its alert action while selecting the owner.
      // BODY after that removal is not a competing user focus request.
      const removedVisibilityAction = visibility && activeElement instanceof HTMLElement
        && !activeElement.isConnected && currentActive === target.ownerDocument.body;
      if (
        request !== this.focusRequest ||
        !this.ownerCurrent() || owner !== this.editingContext || form !== JSON.stringify(this.form) || !this.fieldActive(id) || (visibility || label) && !this.editReady ||
        this.form?.id !== formId ||
        !currentField ||
        !target.isConnected ||
        (currentActive !== activeElement && currentActive !== target && !removedVisibilityAction) ||
        (label && (this.disabled || !this.knownType(currentField)))
      )
        return;
      target.focus();
      if (
        target instanceof HTMLInputElement &&
        target.ownerDocument.activeElement === target
      )
        target.select();
    });
  }
  focusFieldIssue(id: string): void {
    const field = this.fields.find((item) => item.id === id);
    if (!field) return;
    this.selectField(id);
    this.queueFocus({
      id,
      label:
        !this.disabled &&
        this.knownType(field) &&
        (!field.label.trim() || field.label.length > 120),
    });
  }
  focusVisibilityCondition(id: string): void {
    if (!this.ownerCurrent() || !this.editReady || !this.fields.some(field => field.id === id)) return;
    this.selectField(id); this.queueFocus({ id, label: false, visibility: true });
  }
  private queueFocus(request: { id: string; label: boolean; visibility?: boolean }): void {
    this.pendingFocus = { ...request, owner: this.editingContext, form: JSON.stringify(this.form) };
  }
  reviewDependency(id: string): void {
    if (!this.recoveryFields.some(field => field.id === id)) return;
    this.error = ''; this.recovery = null; this.focusVisibilityCondition(id);
  }
  private refuseDependencies(fields: WorkflowCaptureField[], message: string): void {
    this.error = message;
    this.recovery = { ids: fields.map(field => field.id), form: JSON.stringify(this.form), owner: this.editingContext, selection: this.currentSelection() };
  }
  requestLookupSource(field: WorkflowCaptureField): void {
    if (!this.editable || !this.fieldActive(field.id) || !this.lookupSourceAvailable || this.lookupSourceReason || !this.fields.includes(field) || field.type !== 'select') return;
    this.lookupSourceRequested.emit(field.id);
  }
  requestChoiceSource(field: WorkflowCaptureField, event: Event): void {
    const control = event.target as HTMLSelectElement;
    const requested = control.value; control.value = field.lookup ? 'lookup' : 'inline';
    if (!this.editable || !this.fieldActive(field.id) || !this.lookupSourceAvailable || !this.fields.includes(field) || field.type !== 'select') return;
    if (requested === 'lookup') this.requestLookupSource(field);
    else if (requested === 'inline' && field.lookup) this.inlineSourceRequested.emit(field.id);
  }
  private emit(form: WorkflowCaptureForm): void {
    if (!this.editable) return;
    const layoutIssue = this.layoutIssue(form);
    if (layoutIssue) {
      this.error = layoutIssue;
      return;
    }
    this.form = form;
    this.error = '';
    this.recovery = null;
    this.formChange.emit(form);
  }
  initialize(): void {
    if (this.form || this.disabled) return;
    this.emit({
      version: 1,
      id: `form-${crypto.randomUUID()}`,
      title: 'Capture information',
      fields: [],
    });
    this.selectField(null);
  }
  setTitle(title: string): void {
    if (this.form) this.emit({ ...this.form, title });
  }
  knownType(field: WorkflowCaptureField): boolean {
    return this.types.some((type) => type.code === field.type);
  }
  typeLabel(field: WorkflowCaptureField): string {
    return (
      this.types.find((type) => type.code === field.type)?.label ??
      'Unavailable field type'
    );
  }
  options(field: WorkflowCaptureField) {
    return field.options ?? [];
  }
  add(type: WorkflowCaptureField['type']): void {
    if (
      !this.form ||
      this.disabled ||
      this.unsupported ||
      this.fields.length >= 50 ||
      !this.types.some((item) => item.code === type)
    )
      return;
    const field: WorkflowCaptureField = {
      id: `field-${crypto.randomUUID()}`,
      label: this.types.find((item) => item.code === type)!.label,
      type,
      required: false,
      ...(type === 'select' ? { options: [] } : {}),
    };
    this.emit({ ...this.form, fields: [...this.fields, field] });
    this.selectField(field.id);
    this.queueFocus({ id: field.id, label: true });
  }
  update(id: string, change: Partial<WorkflowCaptureField>): void {
    const field = this.fields.find((item) => item.id === id);
    if (!this.form || !field || !this.knownType(field) || !this.fieldActive(id)) return;
    if (Object.hasOwn(change, 'layout') && !this.canEditLayout) return;
    this.emit({
      ...this.form,
      fields: this.fields.map((item) =>
        item.id === id ? { ...item, ...change, id: item.id } : item,
      ),
    });
  }
  dependents(id: string) {
    return this.fields.filter((field) => field.visibleWhen?.fieldId === id);
  }
  changeType(
    field: WorkflowCaptureField,
    type: WorkflowCaptureField['type'],
  ): void {
    const current = this.fields.find((item) => item.id === field.id);
    if (!current) return;
    field = current;
    if (
      !this.knownType(field) || !this.fieldActive(field.id) ||
      !this.fields.some((item) => item.id === field.id) ||
      type === field.type ||
      !this.editable ||
      !this.types.some((item) => item.code === type)
    )
      return;
    if (this.dependents(field.id).length) {
      this.refuseDependencies(this.dependents(field.id), this.dependencyMessage(field.id, 'changing its type'));
      return;
    }
    const next = { ...field, type };
    if (!['text', 'textarea'].includes(type)) delete next.maxLength;
    if (!['number', 'integer'].includes(type)) {
      delete next.minimum;
      delete next.maximum;
    }
    if (type !== 'select') {
      delete next.options;
      delete next.lookup;
    } else next.options = field.type === 'select' ? (field.options ?? []) : [];
    // Update deliberately removes only limits/options incompatible with the newly chosen control.
    if (this.form && !this.disabled && !this.unsupported)
      this.emit({
        ...this.form,
        fields: this.fields.map((item) => (item.id === field.id ? next : item)),
      });
  }
  changeTypeFromControl(field: WorkflowCaptureField, event: Event, model?: NgModel): void {
    const control = event.target as HTMLSelectElement, current = this.fields.find(item => item.id === field.id);
    if (this.editable && this.fieldActive(field.id) && current && current.type === field.type) this.changeType(current, control.value as WorkflowCaptureField['type']);
    const canonical = this.fields.find(item => item.id === field.id)?.type ?? field.type;
    if (control.value !== canonical) {
      // The directive's view-model cache must follow the restored control too;
      // otherwise a later external/Undo change to the refused type is skipped.
      if (model) model.control.setValue(canonical, { emitEvent: false });
      else control.value = canonical;
    }
  }
  private restoringLayoutModel = false;
  private fieldActionsOrigin: string | null = null;
  captureFieldActions(field: WorkflowCaptureField): void {
    this.fieldActionsOrigin = JSON.stringify([this.editingContext, this.form, field.id, this.currentSelection()]);
  }
  runFieldAction(action: 'earlier' | 'later' | 'remove', field: WorkflowCaptureField): void {
    if (!this.editable || !this.fieldActive(field.id) || this.fieldActionsOrigin !==
      JSON.stringify([this.editingContext, this.form, field.id, this.currentSelection()])) return;
    this.fieldActionsOrigin = null;
    if (action === 'remove') this.remove(field); else this.move(field, action === 'earlier' ? -1 : 1);
  }
  remove(field: WorkflowCaptureField): void {
    if (!this.form || !this.editable || !this.fieldActive(field.id)) return;
    const current = this.fields.find((item) => item.id === field.id);
    if (!current) return;
    field = current;
    if (this.dependents(field.id).length) {
      this.refuseDependencies(this.dependents(field.id), this.dependencyMessage(field.id, 'deleting it'));
      return;
    }
    const index = this.fieldPosition(field);
    if (
      this.form.version !== 1 &&
      field.layout?.row !== 'previous' &&
      this.fields[index + 1]?.layout?.row === 'previous'
    ) {
      this.error =
        'Start a new row for the next field before removing the first field in this row.';
      return;
    }
    this.emit({
      ...this.form,
      fields: this.fields.filter((item) => item.id !== field.id),
    });
    this.selectField(
      this.fields[Math.min(index, this.fields.length - 1)]?.id ?? null,
    );
  }
  setFieldOrder(ids: string[]): void {
    if (!this.form || !this.editable || !Array.isArray(ids))
      return;
    const current = this.fields,
      known = new Map(current.map((field) => [field.id, field]));
    if (
      ids.length !== current.length ||
      new Set(ids).size !== current.length ||
      ids.some((id) => !known.has(id)) ||
      ids.every((id, index) => id === current[index].id)
    )
      return;
    let fields = ids.map((id) => known.get(id)!);
    if (this.form.version !== 1) {
      const rows = resolveWorkflowFormLayout(
        this.form.version,
        this.layoutFields(current),
      );
      if (!rows) return;
      const group = new Map(
        current.map((field, index) => [field.id, rows[index].row]),
      );
      const seen = new Set<number>();
      let previous: number | undefined;
      for (const field of fields) {
        const row = group.get(field.id)!;
        if (row !== previous && seen.has(row)) {
          this.error =
            'Keep fields in the same row together. Move the whole row, or start a new row before moving this field.';
          return;
        }
        seen.add(row);
        previous = row;
      }
      // Row identity stays with its contiguous members, rather than joining a
      // new neighbour merely because an order gesture changed the predecessor.
      fields = fields.map((field, index) => {
        const row =
          index > 0 && group.get(fields[index - 1].id) === group.get(field.id)
            ? 'previous'
            : 'new';
        return (field.layout?.row ?? 'new') === row
          ? field
          : { ...field, layout: { ...field.layout, row } };
      });
    }
    const invalid = fields.find(
      (field, index) =>
        field.visibleWhen &&
        !fields
          .slice(0, index)
          .some((earlier) => earlier.id === field.visibleWhen!.fieldId),
    );
    if (invalid) {
      const controller = known.get(invalid.visibleWhen!.fieldId);
      this.refuseDependencies([invalid], `Keep “${controller ? this.fieldLabel(controller) : 'the controlling field'}” before “${this.fieldLabel(invalid)}”, which depends on it.`);
      return;
    }
    this.emit({ ...this.form, fields });
  }
  get canEditLayout(): boolean {
    return (
      this.layoutEnabled &&
      !this.disabled &&
      !this.unsupported &&
      !!this.form &&
      ([2, 3].includes(this.form.version) || this.managedLayout && this.form.version === 1)
    );
  }
  private layoutFields(fields = this.fields) {
    return fields.map((field) => ({
      control: field.type === 'boolean' ? 'checkbox' : field.type,
      layout: field.layout,
    }));
  }
  private layoutIssue(form: WorkflowCaptureForm): string {
    return workflowFormLayoutIssue(
      form.version,
      this.layoutFields(form.fields),
    );
  }
  layoutChoiceReason(
    field: WorkflowCaptureField,
    change: Partial<WorkflowFormLayout>,
  ): string {
    if (this.disabled) return 'This form is read-only.';
    if (!this.canEditLayout)
      return 'Field sizing is unavailable for this saved form.';
    if (!this.fields.includes(field)) return 'Select a current field.';
    const fields = this.fields.map((item) =>
      item === field
        ? { ...item, layout: { ...item.layout, ...change } }
        : item,
    );
    return workflowFormLayoutIssue(
      this.managedLayout && this.form!.version === 1 ? 2 : this.form!.version,
      this.layoutFields(fields),
    );
  }
  setLayout(
    field: WorkflowCaptureField,
    change: Partial<WorkflowFormLayout>,
    model?: NgModel,
  ): void {
    if (this.restoringLayoutModel) return;
    const restore = () => {
      const current = this.fields.find(item => item.id === field.id) ?? field;
      const value = Object.hasOwn(change, 'width') ? current.layout?.width ?? 'fill' : current.layout?.row ?? 'new';
      if (!model) return;
      this.restoringLayoutModel = true;
      try { model.control.setValue(value, { emitEvent: false }); }
      finally { this.restoringLayoutModel = false; }
    };
    if (!this.editable || !this.fieldActive(field.id)) { restore(); return; }
    const reason = this.layoutChoiceReason(field, change);
    if (reason) {
      this.error = reason;
      restore();
      return;
    }
    if (this.managedLayout) {
      this.layoutChangeRequested.emit({ fieldId: field.id, change });
      // The parent accepts one atomic document change; refused gestures retain the saved value.
      restore();
      return;
    }
    this.update(field.id, { layout: { ...field.layout, ...change } });
  }
  rowPosition(field: WorkflowCaptureField): number {
    return this.rowGroups().findIndex((group) =>
      group.some((item) => item.id === field.id),
    );
  }
  get rowCount(): number {
    return this.rowGroups().length;
  }
  private rowGroups(): WorkflowCaptureField[][] {
    if (!this.form || this.form.version === 1)
      return this.fields.map((field) => [field]);
    const rows = resolveWorkflowFormLayout(
      this.form.version,
      this.layoutFields(),
    );
    if (!rows) return [];
    const groups: WorkflowCaptureField[][] = [];
    this.fields.forEach((field, index) => {
      if (!index || rows[index].row !== rows[index - 1].row) groups.push([]);
      groups[groups.length - 1].push(field);
    });
    return groups;
  }
  moveRow(field: WorkflowCaptureField, offset: number): void {
    if (!this.editable || !this.fieldActive(field.id) || !this.canEditLayout || ![-1, 1].includes(offset)) return;
    const groups = this.rowGroups(),
      index = this.rowPosition(field),
      target = index + offset;
    if (index < 0 || target < 0 || target >= groups.length) return;
    groups.splice(target, 0, groups.splice(index, 1)[0]);
    this.setFieldOrder(groups.flat().map((item) => item.id));
  }
  move(field: WorkflowCaptureField, offset: number): void {
    if (!this.editable || !this.fieldActive(field.id)) return;
    const ids = this.fields.map((item) => item.id),
      index = ids.indexOf(field.id),
      target = index + offset;
    if (
      ![-1, 1].includes(offset) ||
      index < 0 ||
      target < 0 ||
      target >= ids.length
    )
      return;
    ids.splice(target, 0, ids.splice(index, 1)[0]);
    this.setFieldOrder(ids);
  }
  private dependencyMessage(
    id: string,
    action: string,
    value?: string,
  ): string {
    const dependents = this.dependents(id).filter(
      (field) => value === undefined || field.visibleWhen?.equals === value,
    );
    return `Update the visibility condition${dependents.length === 1 ? '' : 's'} for ${dependents.map((field) => `“${this.fieldLabel(field)}”`).join(', ')} before ${action}.`;
  }
  choiceOrigin(field: WorkflowCaptureField, index: number): string {
    return JSON.stringify(this.optionOrigin(field, index));
  }
  typeOrigin(field: WorkflowCaptureField): string {
    return JSON.stringify({ formId: this.form?.id, version: this.form?.version, fieldId: field.id, type: field.type, owner: this.ownerGeneration });
  }
  private restoreReconciledNativeControls(): void {
    if (!this.ownerCurrent() || !this.form || this.unsupported) return;
    for (const control of Array.from(this.element.nativeElement.querySelectorAll<HTMLInputElement>('input[data-choice-origin][data-choice-property]'))) {
      let origin: ChoiceOrigin | null = null;
      try { origin = JSON.parse(control.dataset['choiceOrigin'] ?? 'null') as ChoiceOrigin | null; } catch { continue; }
      const field = this.fields.find(item => item.id === origin?.fieldId), property = control.dataset['choiceProperty'];
      if (!origin || !field || field.type !== 'select' || field.lookup != null || !['value', 'label'].includes(property ?? '') || !this.matchesOption(field, origin.index, origin)) continue;
      const canonical = this.options(field)[origin.index][property as 'value' | 'label'];
      // Angular may retain an unchanged binding cache while a rejected optimistic
      // event left another native value. Equal accepted text must retain its caret.
      if (control.value !== canonical) control.value = canonical;
    }
    const elements = this.fieldTypeElements.toArray();
    this.fieldTypeModels.forEach((model, index) => {
      const control = elements[index]?.nativeElement, field = this.fields.find(item => item.id === control?.closest<HTMLElement>('[data-capture-field]')?.dataset['captureField']);
      if (!control || !field || control.dataset['captureTypeOrigin'] !== this.typeOrigin(field)) return;
      if (control.value !== field.type || model.control.value !== field.type || model.viewModel !== field.type)
        model.control.setValue(field.type, { emitEvent: false });
    });
  }
  private optionOrigin(field: WorkflowCaptureField, index: number): ChoiceOrigin | null {
    const option = this.options(field)[index];
    return this.form && option ? { formId: this.form.id, version: this.form.version, fieldId: field.id, index,
      value: option.value, label: option.label, owner: this.ownerGeneration } : null;
  }
  private inlineField(field: WorkflowCaptureField): WorkflowCaptureField | null {
    if (!this.editable || !this.fieldActive(field.id) || field.type !== 'select' || field.lookup != null) return null;
    const current = this.fields.find(item => item.id === field.id);
    return current?.type === 'select' && current.lookup == null ? current : null;
  }
  private matchesOption(field: WorkflowCaptureField, index: number, origin: ChoiceOrigin | null): boolean {
    const option = this.options(field)[index];
    return !!origin && !!option && Number.isInteger(index) && index >= 0 && origin.formId === this.form?.id && origin.version === this.form?.version
      && origin.owner === this.ownerGeneration && origin.fieldId === field.id && origin.index === index
      && origin.value === option.value && origin.label === option.label;
  }
  updateOptionFromInput(field: WorkflowCaptureField, index: number, property: 'value' | 'label', event: Event): void {
    const control = event.target as HTMLInputElement;
    let origin: ChoiceOrigin | null = null;
    try { origin = JSON.parse(control.dataset['choiceOrigin'] ?? 'null') as ChoiceOrigin | null; } catch { /* An obsolete view never supplies a replacement row. */ }
    const before = this.inlineField(field), currentOrigin = !!before && this.matchesOption(before, index, origin);
    this.updateOption(field, index, { [property]: control.value }, origin);
    const current = this.fields.find(item => item.id === field.id), option = current?.type === 'select' && current.lookup == null ? this.options(current)[index] : null;
    const canonical = option?.[property] ?? this.options(field)[index]?.[property] ?? '';
    if (control.value !== canonical) control.value = canonical;
    // Keep consecutive native inputs in one render frame attached to this exact
    // accepted row; Angular refreshes the same view-only expectation on render.
    if (currentOrigin && current && option && this.editable) control.dataset['choiceOrigin'] = this.choiceOrigin(current, index);
  }
  moveOption(field: WorkflowCaptureField, index: number, offset: number): void {
    if (![-1, 1].includes(offset)) return;
    this.reorderOption(field, index, index + offset);
  }
  dropOption(
    field: WorkflowCaptureField,
    event: Pick<CdkDragDrop<unknown>, 'previousIndex' | 'currentIndex'>,
  ): void {
    this.reorderOption(field, event.previousIndex, event.currentIndex);
  }
  private reorderOption(
    field: WorkflowCaptureField,
    from: number,
    to: number,
  ): void {
    const current = this.inlineField(field);
    if (
      !current ||
      current.type !== 'select' ||
      JSON.stringify(this.options(current)) !== JSON.stringify(this.options(field))
    )
      return;
    const options = [...this.options(current)];
    if (
      !Number.isInteger(from) ||
      !Number.isInteger(to) ||
      from < 0 ||
      to < 0 ||
      from >= options.length ||
      to >= options.length ||
      from === to
    )
      return;
    options.splice(to, 0, options.splice(from, 1)[0]);
    this.update(current.id, { options });
  }
  updateOption(
    field: WorkflowCaptureField,
    index: number,
    change: { value?: string; label?: string },
    origin = this.optionOrigin(field, index),
  ): void {
    const current = this.inlineField(field);
    if (!current || !this.matchesOption(current, index, origin)) return;
    field = current;
    if (
      change.value !== undefined &&
      change.value !== this.options(field)[index]?.value &&
      this.dependents(field.id).some(
        (dependent) =>
          dependent.visibleWhen?.equals === this.options(field)[index]?.value,
      )
    ) {
      const value = this.options(field)[index].value;
      this.refuseDependencies(this.dependents(field.id).filter(item => item.visibleWhen?.equals === value), this.dependencyMessage(
        field.id,
        'changing this choice’s stored value',
        value,
      ) + ` Stored value remains “${value}”.`);
      return;
    }
    if (Object.entries(change).every(([key, value]) => this.options(field)[index][key as 'value' | 'label'] === value)) return;
    this.update(field.id, {
      options: this.options(field).map((option, at) =>
        at === index ? { ...option, ...change } : option,
      ),
    });
  }
  addOption(field: WorkflowCaptureField): void {
    const current = this.inlineField(field);
    if (!current) return;
    field = current;
    if (
      field.type === 'select' &&
      field.lookup == null &&
      this.options(field).length < 100
    )
      this.update(field.id, {
        options: [...this.options(field), { value: '', label: '' }],
      });
  }
  removeOption(field: WorkflowCaptureField, index: number): void {
    const origin = this.optionOrigin(field, index), current = this.inlineField(field);
    if (!current || !this.matchesOption(current, index, origin)) return;
    field = current;
    if (
      this.dependents(field.id).some(
        (dependent) =>
          dependent.visibleWhen?.equals === this.options(field)[index]?.value,
      )
    ) {
      this.refuseDependencies(this.dependents(field.id).filter(item => item.visibleWhen?.equals === this.options(field)[index]?.value), this.dependencyMessage(
        field.id,
        'removing this choice',
        this.options(field)[index]?.value,
      ));
      return;
    }
    this.update(field.id, {
      options: this.options(field).filter((_, at) => at !== index),
    });
  }
  controllers(field: WorkflowCaptureField): WorkflowCaptureField[] {
    return this.fields
      .slice(
        0,
        this.fields.findIndex((item) => item.id === field.id),
      )
      .filter((item) => this.knownType(item));
  }
  controller(field: WorkflowCaptureField) {
    return this.controllers(field).find(
      (item) => item.id === field.visibleWhen?.fieldId,
    );
  }
  unavailableMatchingChoice(field: WorkflowCaptureField): boolean {
    const controller = this.controller(field), value = field.visibleWhen?.equals;
    return controller?.type === 'select' && controller.lookup == null && typeof value === 'string' && !!value
      && !this.options(controller).some(option => option.value === value);
  }
  setController(field: WorkflowCaptureField, id: string): void {
    const current = this.fields.find(item => item.id === field.id);
    if (!current || !this.editable || !this.fieldActive(field.id) || JSON.stringify(current.visibleWhen) !== JSON.stringify(field.visibleWhen)) return;
    field = current;
    if (!id) {
      this.update(field.id, { visibleWhen: null });
      return;
    }
    const controller = this.controllers(field).find((item) => item.id === id);
    if (!controller) return;
    this.update(field.id, {
      visibleWhen: {
        ...field.visibleWhen,
        fieldId: id,
        equals: controller.type === 'boolean' ? false : '',
      },
    });
  }
  setEquals(
    field: WorkflowCaptureField,
    equals: string | number | boolean | null,
  ): void {
    const current = this.fields.find(item => item.id === field.id);
    if (!current || !this.editable || !this.fieldActive(field.id) || JSON.stringify(current.visibleWhen) !== JSON.stringify(field.visibleWhen)) return;
    field = current;
    if (field.visibleWhen)
      this.update(field.id, {
        visibleWhen: { ...field.visibleWhen, equals: equals ?? '' },
      });
  }
  copy(): void {
    if (this.disabled || this.unsupported || !this.form) return;
    const choices = this.copies.filter(
      (copy) => copy.key === this.selectedCopy,
    );
    if (choices.length !== 1) return;
    const source = choices[0].form;
    if (isUnsupportedCaptureForm(source)) {
      this.error = 'This saved form cannot be copied by this editor.';
      return;
    }
    this.emit({
      ...structuredClone(source),
      id: `form-${crypto.randomUUID()}`,
    });
    this.selectedCopy = '';
    this.selectField(null);
  }
  issue(field: WorkflowCaptureField): string {
    return this.validation.fields[field.id] ?? '';
  }
  trackCopy(_: number, copy: WorkflowCaptureFormCopy) {
    return copy.key;
  }
  trackField(_: number, field: WorkflowCaptureField) {
    return field.id;
  }
  trackOption(index: number) {
    return index;
  }
}
